import React, { useEffect, useMemo, useState } from 'react';
import styles from './Favorites.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import Title from '../../shared/Title/Title';
import PreMatchFilters from './Filters/PreMatchFilters';
import Table from './Table/Table';
import ContentSpinner from '../../shared/Spinner/Spinner';
import { LANGUAGES } from '../../../constants/defaultValues';
import { changeEventFilters, setSportRefId } from '../../../store/actions/sportEvents';
import { changeFavoritesEventType } from '../../../store/actions/favorites';
import { setSocketContext } from '../../../store/actions/socketAction';
import ConnectionError from '../../shared/Events/ConnectionError/ConnectionError';
import SportTitle from '../Events/SportTitle/SportTitle';
import classnames from 'classnames';
import LiveFilters from './Filters/LiveFilters';
import {
    EVENT_FILTER_OPTIONS,
    getInitialEventFilters,
    getSavedEventFilters,
    mergeEventFilters,
    saveEventFilters,
} from '../../../constants/event-filters';

const favoritesSportId = 'favorites';

const Favorites = () => {
    const dispatch = useDispatch();

    const locale = useSelector(state => state.settings.locale);
    const { filters, isLive } = useSelector(state => state.sportEvents);
    const { isSocketConnected, connectionCounter } = useSelector(state => state.socket);

    const { events, isFetching, sortType } = useSelector(state => state.favorites);

    const favorites = useMemo(() => events.filter(event => event.live === isLive), [events]);

    const [filteredEvents, setFilteredEvents] = useState([]);

    const filterByCategory = (event, categories) => {
        const selectedCategory = categories.find(category => category.selected).id;
        return selectedCategory === 'all' || event.league.categoryId === selectedCategory;
    };

    const filterByLeague = (event, leagues) => {
        const selectedLeague = leagues.find(league => league.selected).id;
        return selectedLeague === 'all' || event.league.id === selectedLeague;
    };

    const filterByPeriod = (event, periods) => {
        const selectedPeriod = periods.find(period => period.selected).getRange();
        const { startTime, endTime } = selectedPeriod;
        const eventStartTime = event.startedAt * 1000;
        return (!startTime || eventStartTime > startTime) && (!endTime || eventStartTime < endTime);
    };

    const renderEvents = () => {
        if (sortType === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
            return (
                <>
                    {filters.leagues?.map(league => {
                        const leagueEvents = filteredEvents.filter(
                            fvrt => fvrt.league.id === league.id
                        );

                        return leagueEvents.length ? (
                            <Table
                                key={league.id}
                                countryCode={league.countryCode}
                                categoryName={league.categoryName}
                                leagueName={league.seasonName}
                                events={leagueEvents}
                            />
                        ) : null;
                    })}
                </>
            );
        } else if (
            sortType === EVENT_FILTER_OPTIONS.orders.byTime.id ||
            sortType === EVENT_FILTER_OPTIONS.orders.byMbn.id
        ) {
            return <Table events={filteredEvents} />;
        }
    };

    useEffect(() => {
        if (isLive === null) return;

        const categories = {};
        const leagues = {};

        favorites.forEach(favorite => {
            categories[favorite.league.categoryId] = {
                id: favorite.league.categoryId,
                label: favorite.league.categoryName,
                countryCode: favorite.league.countryCode,
            };
            leagues[favorite.league.id] = {
                ...favorite.league,
                names: {
                    [locale]: favorite.league.seasonName,
                },
            };
        });

        const filters = {
            categories: Object.values(categories),
            leagues: Object.values(leagues),
        };

        const isESports = false;

        const currentFilters = getInitialEventFilters(isLive, isESports);
        const savedFilters = getSavedEventFilters({
            sportRefId: favoritesSportId,
            isLive: isLive,
        });
        const mergedFilters = mergeEventFilters(currentFilters, filters, savedFilters);

        dispatch(changeEventFilters({ filters: mergedFilters, shouldUpdateEvents: false }));
        dispatch(changeFavoritesEventType(mergedFilters.orders.find(order => order.selected).id));
    }, [favorites, isLive]);

    useEffect(() => {
        if (!Object.keys(filters).length) return;

        saveEventFilters({ filters, sportRefId: favoritesSportId, isLive });

        const { categories, leagues, periods } = filters;
        const filteredEvents = favorites.filter(fvrt =>
            isLive
                ? filterByLeague(fvrt, leagues)
                : filterByCategory(fvrt, categories) &&
                  filterByLeague(fvrt, leagues) &&
                  filterByPeriod(fvrt, periods)
        );
        setFilteredEvents(filteredEvents);
    }, [filters]);

    useEffect(() => {
        const eventIds = favorites.map(event => event.id);

        dispatch(setSocketContext({ events: eventIds }));
    }, [favorites.length]);

    useEffect(() => {
        dispatch(setSportRefId('favorites'));
    }, []);

    return (
        <div
            className={classnames(styles.container, {
                [styles.live]: isLive,
            })}
        >
            {isLive ? (
                <>
                    <SportTitle title={'Favorites'} isLive sportId={'favorites'} />
                    <LiveFilters />
                </>
            ) : (
                <>
                    <Title title={'Favorites'} />
                    <PreMatchFilters />
                </>
            )}
            <div
                className={classnames(styles.content, {
                    [styles.live]: isLive,
                })}
            >
                {isFetching || !connectionCounter ? (
                    <ContentSpinner />
                ) : isSocketConnected ? (
                    <div>{renderEvents()}</div>
                ) : (
                    <ConnectionError />
                )}
            </div>
        </div>
    );
};

export default Favorites;
